import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpStatus } from '../../common/enums/HttpStatus';
import {
  ISupportTicket,
  ISupportTicketListQuery,
  ISupportTicketListResponse,
  ITicketReplyUser,
} from '../../common/interfaces/TechnicalSupport.interface';
import { createBaseQuery } from './utils/createBaseQuery';

/**
 * Build the request params for the ticket list: pagination always, plus any set server-side filter.
 * Drops `'ALL'` and empty values, and maps Date inputs to ISO strings (the backend coerces them).
 * Pure + exported so the mapping is unit-testable without rendering the page.
 */
export const buildTicketListParams = (
  args: ISupportTicketListQuery
): Record<string, string | number> => {
  const params: Record<string, string | number> = {
    page: args.page,
    size: args.size,
  };
  const set = (key: string, value?: string | number) => {
    if (
      value !== undefined &&
      value !== null &&
      value !== '' &&
      value !== 'ALL'
    ) {
      params[key] = value;
    }
  };
  const toIso = (d?: string | Date) =>
    d instanceof Date ? d.toISOString() : d;

  set('status', args.status);
  set('category', args.category);
  set('search', args.search?.trim());
  set('fromDate', toIso(args.fromDate));
  set('toDate', toIso(args.toDate));
  return params;
};

export const technicalSupportApi = createApi({
  reducerPath: 'TechnicalSupportApi',
  baseQuery: createBaseQuery('/support-tickets'),
  tagTypes: ['TechnicalSupport'],
  endpoints: (builder) => ({
    fetchTechnicalSupport: builder.query<
      { code: HttpStatus; data: ISupportTicketListResponse },
      ISupportTicketListQuery
    >({
      query: (args) => ({
        url: '',
        params: buildTicketListParams(args),
      }),
      providesTags: (result) =>
        result
          ? [
              { type: 'TechnicalSupport' as const, id: 'LIST' },
              ...result.data.items.map(({ id }) => ({
                type: 'TechnicalSupport' as const,
                id,
              })),
            ]
          : [{ type: 'TechnicalSupport' as const, id: 'LIST' }],
    }),

    fetchTicketById: builder.query<
      { code: HttpStatus; data: ISupportTicket },
      string
    >({
      query: (id) => `/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'TechnicalSupport', id }],
    }),

    updateTicketStatus: builder.mutation<
      { code: HttpStatus; data: ISupportTicket },
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `/${id}/status`,
        method: 'PATCH',
        body: { status },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'TechnicalSupport', id },
        { type: 'TechnicalSupport', id: 'LIST' },
      ],
    }),

    addTicketReply: builder.mutation<
      { code: HttpStatus; data: ISupportTicket },
      { id: string; message: string; to: string; fromUser: ITicketReplyUser }
    >({
      query: ({ id, message, to }) => ({
        url: `/${id}/reply`,
        method: 'PUT',
        body: { message, to },
      }),
      onQueryStarted: async (
        { id, message, to, fromUser },
        { dispatch, queryFulfilled }
      ) => {
        const patchResult = dispatch(
          technicalSupportApi.util.updateQueryData(
            'fetchTicketById',
            id,
            (draft) => {
              if (!draft.data.replies) draft.data.replies = [];
              draft.data.replies.push({
                from: fromUser.id,
                to,
                fromUser,
                toUser: { id: to },
                message,
                sendAt: new Date().toISOString(),
              });
            }
          )
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'TechnicalSupport', id },
        { type: 'TechnicalSupport', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useFetchTechnicalSupportQuery,
  useFetchTicketByIdQuery,
  useUpdateTicketStatusMutation,
  useAddTicketReplyMutation,
} = technicalSupportApi;
